import React, { useEffect, useState } from 'react';
import { appointmentService } from '../services/appointmentService';
import { Service, ServiceCategory, Professional } from '../services/appointmentService';

interface Props {
  service?: Partial<Service> | null;
  categories: ServiceCategory[];
  onSaved: (saved: Service) => void;
  onCancel?: () => void;
  branch?: 'antofagasta' | 'santiago';
}

const ServiceForm: React.FC<Props> = ({ service, categories, onSaved, onCancel, branch = 'antofagasta' }) => {
  const [form, setForm] = useState<Partial<Service>>({
    name: '',
    description: '',
    duration_minutes: 30,
    price: 0,
    category: undefined,
    professionals: [],
  });
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loadingProfessionals, setLoadingProfessionals] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  // Cargar lista de profesionales al montar el componente
  useEffect(() => {
    const loadProfs = async () => {
      setLoadingProfessionals(true);
      try {
        const data = branch === 'santiago'
          ? await appointmentService.getAllProfessionalsSantiago()
          : await appointmentService.getAllProfessionals();
        setProfessionals(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error cargando profesionales', err);
        setProfessionals([]);
      } finally {
        setLoadingProfessionals(false);
      }
    };
    loadProfs();
  }, [branch]);

  useEffect(() => {
    if (service) {
      setForm({
        name: service.name ?? '',
        description: service.description ?? '',
        duration_minutes: service.duration_minutes ?? 30,
        price: service.price ?? 0,
        category: (service as any).category ?? undefined,
        professionals: (service as any).professionals ?? [],
        id: (service as any).id,
      });
    } else {
      setForm({ name: '', description: '', duration_minutes: 30, price: 0, category: undefined, professionals: [] });
    }
  }, [service]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === 'price' || name === 'duration_minutes') {
      const n = Number(value);
      setForm(prev => ({ ...prev, [name]: isNaN(n) ? value : n }));
    } else if (name === 'category') {
      setForm(prev => ({ ...prev, category: value ? Number(value) : undefined }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }
    setErrors(prev => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const handleProfessionalToggle = (professionalId: number) => {
    setForm(prev => {
      const currentProfessionals = prev.professionals || [];
      const isSelected = currentProfessionals.includes(professionalId);
      
      if (isSelected) {
        // Remover profesional
        return { ...prev, professionals: currentProfessionals.filter(id => id !== professionalId) };
      } else {
        // Agregar profesional
        return { ...prev, professionals: [...currentProfessionals, professionalId] };
      }
    });
  };

  const validate = () => {
    const e: { [k: string]: string } = {};
    if (!form.name || !String(form.name).trim()) e.name = 'El nombre es obligatorio';
    if (!form.duration_minutes) e.duration_minutes = 'La duración es obligatoria';
    if (form.price == null) e.price = 'El precio es obligatorio';
    if (!form.category) e.category = 'La categoría es obligatoria';
    return e;
  };

  const handleSubmit = async (ev?: React.FormEvent) => {
    ev?.preventDefault();
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSaving(true);
    setGeneralError(null);
    try {
      let saved: any;
      const payload = {
        name: form.name as string,
        description: form.description,
        duration_minutes: Number(form.duration_minutes),
        price: Number(form.price),
        category: Number(form.category),
        professionals: form.professionals || [],
      };

      if ((form as any).id) {
        saved = branch === 'santiago'
          ? await appointmentService.updateServiceSantiago((form as any).id, payload)
          : await appointmentService.updateService((form as any).id, payload);
      } else {
        saved = branch === 'santiago'
          ? await appointmentService.createServiceSantiago(payload)
          : await appointmentService.createService(payload);
      }
      onSaved(saved);
    } catch (err: any) {
      console.error('Error saving service', err);
      const data = err?.response?.data;
      if (data && typeof data === 'object') {
        const next: { [k: string]: string } = {};
        Object.keys(data).forEach(k => {
          const v = (data as any)[k];
          next[k] = Array.isArray(v) ? String(v[0]) : String(v);
        });
        setErrors(next);
      } else {
        // Mensaje general
        const msg = err?.response?.statusText || err?.message || 'Error al guardar el servicio';
        setGeneralError(String(msg));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h3 className="text-lg font-semibold mb-4">{(form as any).id ? 'Editar Servicio' : 'Crear Servicio'}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {generalError && (
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-800">{generalError}</p>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
          <input name="name" value={form.name as string} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg`} />
          {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
          <textarea name="description" value={form.description as string} onChange={handleChange} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Duración (min)</label>
            <input name="duration_minutes" value={String(form.duration_minutes ?? '')} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.duration_minutes ? 'border-red-500' : 'border-gray-300'} rounded-lg`} />
            {errors.duration_minutes && <p className="text-sm text-red-600 mt-1">{errors.duration_minutes}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Precio</label>
            <input name="price" value={String(form.price ?? '')} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.price ? 'border-red-500' : 'border-gray-300'} rounded-lg`} />
            {errors.price && <p className="text-sm text-red-600 mt-1">{errors.price}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Categoría</label>
            <select name="category" value={String(form.category ?? '')} onChange={handleChange} className={`w-full px-3 py-2 border ${errors.category ? 'border-red-500' : 'border-gray-300'} rounded-lg`}>
              <option value="">Selecciona una categoría</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
            {errors.category && <p className="text-sm text-red-600 mt-1">{errors.category}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Profesionales encargados
          </label>
          {loadingProfessionals ? (
            <p className="text-sm text-gray-500">Cargando profesionales...</p>
          ) : professionals.length === 0 ? (
            <p className="text-sm text-gray-500">No hay profesionales disponibles. Crea uno primero en la pestaña "Profesionales".</p>
          ) : (
            <div className="border border-gray-300 rounded-lg p-4 max-h-64 overflow-y-auto bg-gray-50">
              <div className="space-y-2">
                {professionals.map((prof) => {
                  const isChecked = (form.professionals || []).includes(prof.id!);
                  return (
                    <label
                      key={prof.id}
                      className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${
                        isChecked ? 'bg-pink-50 border border-pink-300' : 'bg-white border border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleProfessionalToggle(prof.id!)}
                        className="w-4 h-4 text-pink-600 border-gray-300 rounded focus:ring-pink-500"
                      />
                      <span className={`ml-3 text-sm ${isChecked ? 'font-medium text-pink-900' : 'text-gray-700'}`}>
                        {prof.first_name} {prof.last_name}
                      </span>
                      {prof.email && (
                        <span className="ml-auto text-xs text-gray-500">{prof.email}</span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <p className="text-xs text-gray-500 mt-2">
            Selecciona los profesionales que pueden realizar este servicio. Si no seleccionas ninguno, cualquier profesional podrá realizarlo.
          </p>
        </div>

        <div className="flex justify-end space-x-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 bg-gray-100 rounded-lg">Cancelar</button>
          <button type="submit" disabled={saving} className="px-4 py-2 bg-gray-900 text-white rounded-lg">{saving ? 'Guardando...' : 'Guardar'}</button>
        </div>
      </form>
    </div>
  );
};

export default ServiceForm;
